import { GetSyncLogsResult } from 'src/store/integrations-v2/types';

export type IntegrationStateType = 'disconnected' | 'resume' | 'manage';
export type FieldTypes =
  | 'Date'
  | 'Integer'
  | 'Long'
  | 'String'
  | 'Set'
  | 'List'
  | 'Object'
  | 'Boolean'
  | 'Double';

export type IntegrationType = 'CRM' | 'CMP' | 'AUTH';

/**
 * Enum for sync direction configuration
 * - BIDIRECTIONAL: Both inbound and outbound sync (default for CRMs)
 * - INBOUND_ONLY: Only sync data from provider to Sprouts (e.g., Gong)
 * - OUTBOUND_ONLY: Only sync data from Sprouts to provider (e.g., Instantly, HeyReach)
 * - AUTH_ONLY: Authentication-only integrations (e.g., Gmail, Slack, Amplitude)
 *   - No data sync, no configuration stages
 *   - Just OAuth authentication and permission grant
 *   - Automatically marked as CONFIGURED after successful auth
 */
export enum SyncDirection {
  BIDIRECTIONAL = 'BIDIRECTIONAL',
  INBOUND_ONLY = 'INBOUND_ONLY',
  OUTBOUND_ONLY = 'OUTBOUND_ONLY',
  AUTH_ONLY = 'AUTH_ONLY',
}

export interface IIntegrationTool {
  /**
   * This is the name of the tool, it will be used as integration param in the page routes
   */
  id: string;

  /**
   * provider name is the key which is used to trigger apis for a perticular integration
   */
  providerName: string;

  /**
   * Label of the integration you want to show in the ui
   */
  label: string;

  /**
   * Integration logo shown on the Card and other places
   */
  CardLogo: string;

  oauthEnabled: boolean;

  /**
   * Type of integration
   */
  type: IntegrationType;
  integrationStatus: string;

  /**
   * Url to trigger OAuth (can be async for secure state storage)
   */
  connectUrl: string | (() => string | Promise<string>);

  activeStep: number;

  /**
   * Feature flag name saved in the DB for current integration
   */
  featureFlag: string;
  syncData: GetSyncLogsResult[];
  lastSyncedAt?: string;
  lastAutoSyncAt?: string;

  /**
   * List of the fields that are of dropdown/singleSelect/Multiselect type in the integration database
   */
  dropdownFieldTypes: string[];
  syncDataLoading: boolean;

  /**
   * Api key input description if integration does not support OAuth
   */
  ApiKeyDescription?: string;
  DomainDescription1?: string;
  DomainDescription2?: string;

  /**
   * Types Match for Sprouts and Integration
   */
  fieldTypes: Record<FieldTypes, string[]>;
  requiredIntegrationFields: Record<string, string[]>;
  /**
   * Supported objects for synchronization (with frontend field mapping)
   */
  supportedObjects?: string[];
  /**
   * Supported activity entities (includes backend-only entities like Sequences, SequenceSteps)
   */
  supportedActivity?: string[];
  /**
   * Supported activities for sync scenario UI
   */
  syncScenarioActivities?: string[];
  /**
   * Supported stages for the integration flow
   */
  supportedStage?: string[];
  /**
   * Sync direction for the integration
   * - BIDIRECTIONAL: Both inbound and outbound sync (default for CRMs)
   * - INBOUND_ONLY: Only sync data from provider to Sprouts (e.g., Gong)
   * - OUTBOUND_ONLY: Only sync data from Sprouts to provider (e.g., Instantly, HeyReach)
   * When set to INBOUND_ONLY or OUTBOUND_ONLY, SYNC_RULE_SETUP stage is automatically excluded
   */
  syncDirection?: SyncDirection;
  totalSyncData?: number;
  totalPages?: number;
  /**
   * Configuration for authentication (OAuth, API Key, etc.)
   * Used to generate dynamic forms and JSON payloads
   */
  authConfig?: AuthConfig;
  /**
   * Flag to indicate if this is a new integration (shows "NEW" badge)
   */
  isNew?: boolean;
  /**
   * Flag to indicate if this integration supports conditional sync (inbound/outbound filtering)
   * Only HubSpot, Salesforce, and Microsoft support conditional sync currently
   */
  supportsConditionalSync?: boolean;
  /**
   * Flag to indicate if this integration supports syncing campaign activity data (Calls, Meetings, Tasks, Emails)
   * Applicable to CRMs that can receive activity data from campaign management platforms
   */
  supportsActivitySync?: boolean;
  /**
   * Flag to indicate if this integration supports Lead entity (separate from Contacts)
   * Applicable to CRMs that distinguish between Contacts and Leads
   */
  supportsLeadEntity?: boolean;
  /**
   * Flag to indicate if this integration supports manual sync mode (sync automatically vs sync manually)
   * Currently only applicable to Salesforce integrations
   */
  supportsManualSync?: boolean;
}

export type AuthFieldType = 'text' | 'password' | 'dropdown' | 'url' | 'file';

export enum AuthStage {
  PRE_AUTH = 'PRE_AUTH',   // Inputs needed before OAuth (e.g., Tenant ID)
  POST_AUTH = 'POST_AUTH', // Inputs needed after OAuth (e.g., Location)
  CONFIG = 'CONFIG',       // Standard API Key / Domain
}

export interface AuthField {
  key: string;          // JSON key for the payload (e.g., 'api_key', 'subdomain')
  label: string;        // UI Label
  type: AuthFieldType;
  required: boolean;
  placeholder?: string;
  description?: string; // Helper text
  options?: { label: string; value: string }[]; // For dropdowns
  validationRegex?: string; // Optional regex for frontend validation
  stage?: AuthStage; // Default to CONFIG if undefined
  payloadKey?: string; // Key to map this field to in the backend payload (for legacy compatibility)
}

export interface AuthConfig {
  mode: 'OAUTH' | 'API_KEY' | 'HYBRID' | 'DIRECT'; // HYBRID allows mixed or choice
  fields: AuthField[]; // Dynamic input fields
  oauth?: {
    url: string | ((data: Record<string, any>) => string | Promise<string>);      // Authorization URL (can be async)
    clientId?: string; // Optional: if frontend needs it
    scopes?: string[]; // Optional: requested scopes
  };
  needsSelection?:boolean;
}

export type IntegrationsToolsType = Record<string, IIntegrationTool>;

export interface IFooterButtonConfig {
  text: string;
  onClick: () => void;
  loading?: boolean;
}

export interface MappingFieldExtraInfo {
  label: string;
  tooltipTitle: string;
  tooltipDescription: string;
}

export interface IntegrationTableColumn {
  label: string;
  id: string;
  width?: number;
  minWidth?: number;
  maxWidth?: number;
  type: string;
  align?: 'right' | 'left' | 'center' | 'inherit' | 'justify';
  prefix?: string;
  format?: (value: number | string) => number | string;
  autoCompleteplaceholder?: string;
  startIcon?: string;
}
export type Option = {
  label: string;
  value: string;
};
export type RowType = Record<string, any>;
export type CommonDataType = Record<string, Option[]>;

export interface ITableData {
  rowData: RowType[];
  commonData: CommonDataType;
}

export type OnTableUpdate = (row: RowType, columnId: string, data: any) => void;

export type MapValuesType = 'Others' | 'dealStage' | 'campaignStage';
export type SproutsFieldDataType = 'string' | 'number' | 'dropdown' | 'date';
export type PropertyAction = 'All' | 'Create' | 'Update' | 'None';

export type SproutsFieldMocType = {
  label: string;
  id: string;
  typeId?: string;
  fieldType?: string;
  sproutsFieldType?: SproutsFieldDataType;
  mapValues?: MapValuesType;
  defaultMapValues?: MapValuesType;
  isAiField?: boolean;
  isIcpField?: boolean;
  isCustomField?: boolean;
  isAccountField?: boolean;
  featureFlag?: string;
  /**
   * user need to map this field if reqired is true, otherwise show error
   */
  required?: boolean;

  /**
   * Use this to show data on test run screen
   */
  testValue?: string;

  /**
   * some field can support multiple types during mapping
   */
  supportedTypes?: FieldTypes[];

  /**
   * if true disable outbound switch Since Sprouts is the source of truth and is getting enriched via cataloger.
   */
  onlyOutbound?: boolean;

  /**
   * if true, disables the "Only if blank in CRM" option in the Sprouts to CRM dropdown
   * Forces "Always" sync mode (from_sprouts) as the only option
   * Used for activity entities where data should always sync from Sprouts
   */
  disableToCrmIfBlank?: boolean;

  /**
   * if its a custom dropdown field, this will be the options for value mapping
   */
  customeFieldValues?: Record<string, (string | number)[]>;

  deprecating?: boolean;
  /**
   * Activity type identifier for filtering based on globalSyncFlag
   * Uses standard object keys: 'Contacts', 'Accounts', 'Deals', 'Leads', 'Tasks', 'Meetings', 'Emails', 'DealsContacts'
   */
  activityType?: string;


  selectedData: {
    value: string;
    // sprouts to integration
    to_crm_if_blank: boolean;
    from_sprouts: boolean;
    nosync: boolean;
    // crm to sproutes
    dontSyncToSprouts: boolean;
    toSprouts: boolean;
  };

  /**
   * if its a reqired field and optional if with other field
   * i.e. either you can select this or other then pass the id of those fields in the array
   */
  optionalWithValue?: string;
  extraInfo?: MappingFieldExtraInfo;

  /**
   * Property action from CRM indicating editability
   * - None: Field is read-only, outbound sync should be disabled
   * - Create: Field can only be set during creation
   * - Update: Field can only be updated
   * - All: Field supports both create and update operations
   */
  propertyAction?: PropertyAction;
};
export type FieldMappingsMockUpType = {
  id: string;
  label: string;
  testRunId: string;
  testRunLabel: string;
  icon: string;
  desc: string;
  count: string;
  entity: string;
  sproutsFields: SproutsFieldMocType[];
};

type EntityName = 'Account' | 'Contact' | 'Opportunity' | 'Lead';
export type WarningsType = Record<EntityName, number> & { [key: string]: number };

type WarningDetailCategory = 'Mismatch' | 'Pending' | 'ICPScoring';

export type WarningDetailsType = Record<EntityName, Record<WarningDetailCategory, string[]>> & {
  [key: string]: Record<WarningDetailCategory, string[]>;
};

export interface FilterOptions {
  startDate: any;
  endDate: any;
  status: string;
  source: string;
  page: number;
  size: number;
  entity: string;
}

export const SYNC_SCENARIO_COLUMNS: {
  label: string;
  key: 'object' | 'create' | 'update';
}[] = [
    {
      label: 'Object',
      key: 'object',
    },
    {
      label: 'Create',
      key: 'create',
    },
    {
      label: 'Update',
      key: 'update',
    },
  ];

/**
 * Connection test result for AUTH_ONLY integrations
 * Contains verification status and fetched metadata
 */
export interface ConnectionTestResult {
  success: boolean;
  provider: string;
  metadata: ConnectionMetadata;
  summary: string;
  error?: string;
  testedAt: string;
}

/**
 * Metadata structure for different AUTH_ONLY integrations
 */
export interface ConnectionMetadata {
  // Gmail
  email?: string;
  totalMessages?: number;
  labels?: string[];

  // Slack
  workspaceName?: string;
  domain?: string;
  memberCount?: number;
  channelCount?: number;
  teamId?: string;

  // Amplitude
  organizationName?: string;
  projects?: Array<{ id: string; name: string }>;
  eventCount?: number;

  // Segment
  sourceCount?: number;
  destinationCount?: number;
  monthlyEvents?: number;
  workspaceId?: string;

  // Mixpanel
  projectId?: string;
  projectName?: string;
  totalEvents?: number;
  dataRetentionDays?: number;

  // Common fields
  connectedUser?: string;
  connectedAt?: string;
}

// Activity types for sync-scenario
export type ActivityType = 'Calls' | 'Meetings' | 'Tasks' | 'Emails';

// Global sync flag keys for activities
export type ActivityFlagKey = 'callEnabled' | 'meetingEnabled' | 'taskEnabled' | 'emailEnabled' | 'emailAsTaskEnabled';

// Global sync flag interface for activities
export interface ActivityGlobalSyncFlag {
  callEnabled?: boolean;
  meetingEnabled?: boolean;
  taskEnabled?: boolean;
  emailEnabled?: boolean;
  emailAsTaskEnabled?: boolean;
}

// Props for ActivityCheckboxes component
export interface ActivityCheckboxesProps {
  syncScenarioActivities: string[];
  globalSyncFlag: ActivityGlobalSyncFlag;
  onSyncOptionChange: (option: string, isChecked: boolean) => void;
  onEmailTypeChange: (isTask: boolean) => void;
  disabled: boolean;
}

// Configuration for each activity type in sync-scenario
export interface ActivityConfig {
  label: string;
  flagKey: ActivityFlagKey;
  hasSubOptions?: boolean;
}

// SyncScenarioTable component types
export type FieldOption = {
  value: string;
  label: string;
  type: string;
};

export type OperatorOption = {
  value: string;
  label: string;
};
